import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Layout, Card, Typography, Input, DatePicker, Button, Space, Steps, Badge, Modal, Table, App, Collapse, Tabs, Row, Col } from 'antd'
import { ClockCircleOutlined, PictureOutlined, PrinterOutlined, ReloadOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import Navbar from '../../components/Navbar'
import Sidebar from '../../components/Sidebar'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useTheme } from '../../contexts/ThemeContext'
import { notaanota, lotesRelatorio, notaMetragem } from '../../services/api'
import { abrirRelatorioLote } from '../Relatorios/RelatorioLote'

import ModalLotes from './components/ModalLotes'
import ConferenciaExpedicaoTab from './components/ConferenciaExpedicaoTab'
import '../../styles/timeline.css'

const { Content } = Layout
const { Title, Text } = Typography

const PainelExpedicao = ({ embedded = false }) => {
  const navigate = useNavigate()
  const { message } = App.useApp()
  const { user } = useAuth()
  const { currentTheme } = useTheme()
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [sidebarVisible, setSidebarVisible] = useState(false)
  const [selectedDate, setSelectedDate] = useState(dayjs())
  const [loading, setLoading] = useState(false)
  const [empresa, setEmpresa] = useState(user?.assignedBranch?.codigo ?? user?.empresa ?? '')
  const [dadosExpedicao, setDadosExpedicao] = useState([])
  const [dadosNormalizados, setDadosNormalizados] = useState([]) // Estado global para dados normalizados - pode ser reutilizado em toda a página
  const [currentTime, setCurrentTime] = useState(new Date().toLocaleTimeString())
  const [modalVisible, setModalVisible] = useState(false)
  const [carroSelecionado, setCarroSelecionado] = useState(null)
  const [notasCarroSelecionado, setNotasCarroSelecionado] = useState([])
  const [lotesPorCarro, setLotesPorCarro] = useState({})
  const [loadingLotes, setLoadingLotes] = useState(false)

  // Estados para Cubagem
  const [cubagemData, setCubagemData] = useState({ totalRegistros: 0, totalClientes: 0, totalNotas: 0, data: [] })
  const [cubagemLoading, setCubagemLoading] = useState(false)
  const [cubagemModalVisible, setCubagemModalVisible] = useState(false)
  const [cubagemFilters, setCubagemFilters] = useState({ cliente: '', nota: '', usuario: '' })

  // Estados para modal de lotes
  const [modalLotesVisible, setModalLotesVisible] = useState(false)
  const [carroSelecionadoLotes, setCarroSelecionadoLotes] = useState(null)
  const [dadosCarroLotes, setDadosCarroLotes] = useState([])

  // Ref para controlar se a mensagem de sucesso já foi exibida
  const messageShownRef = useRef(false)
  // Guardar para evitar requisições duplicadas em modo desenvolvimento (StrictMode)
  const initialFetchDoneRef = useRef(false)

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date().toLocaleTimeString())
    }, 1000)
    return () => clearInterval(timer)
  }, [])

  // Atualizar empresa quando o usuário mudar
  useEffect(() => {
    const codigo = user?.assignedBranch?.codigo ?? user?.empresa
    if (codigo != null) {
      setEmpresa(codigo)
    }
  }, [user])

  // Carregar dados iniciais uma única vez quando a empresa estiver disponível
  useEffect(() => {
    if (empresa && !initialFetchDoneRef.current) {
      initialFetchDoneRef.current = true
      buscarDadosBackend()
    }
  }, [empresa])

  // Escutar mudanças no estado da sidebar para refletir colapso/expansão
  useEffect(() => {
    const handleSidebarToggle = (event) => {
      setSidebarCollapsed(event.detail.collapsed)
    }
    window.addEventListener('sidebarToggle', handleSidebarToggle)
    return () => window.removeEventListener('sidebarToggle', handleSidebarToggle)
  }, [])

  // Função unificada para buscar dados do backend
  const buscarDadosBackend = async (dataEspecifica = null) => {
    try {
      setLoading(true)

      // Usar a data específica ou a data selecionada
      const dataParaConsulta = dataEspecifica || selectedDate

      let dataFormatada

      if (dataParaConsulta) {
        dataFormatada = dataParaConsulta.format('YYYY-MM-DD')
      } else {
        // Se não há data selecionada, usar a data atual
        const hoje = new Date()
        const ano = hoje.getFullYear()
        const mes = String(hoje.getMonth() + 1).padStart(2, '0')
        const dia = String(hoje.getDate()).padStart(2, '0')
        dataFormatada = `${ano}-${mes}-${dia}`
      }

      // Fazer requisição para o backend (onboard/dia) para dados de Expedição
      const responseOnboard = await notaanota.getOnboardPorDia(dataFormatada, empresa)

      // Normalizar dados para Expedição
      const dadosNormalizadosTemp = (Array.isArray(responseOnboard) ? responseOnboard : []).map(item => ({
        ...item,
        // Mapear REMETENTE -> CLIENTE para componentes
        CLIENTE: item.REMETENTE,
        // Garantir campos esperados: CD e Praca
        CD: item.CD ?? item.cd ?? '',
        Praca: item.Praca ?? item.praca ?? '',
        // Sinalizar status de etiqueta com base nos totais
        STATUS_ETIQUETA: (Number(item.STATUS) === 1 && (parseInt(item.TTL_ETIQUETAS_IMP) || 0) > 0) ? 1 : 0,
        // Usar última etiqueta como referência de data
        DATA_ETIQUETA: item.ULTIMA_ETIQUETA
      }))


      // Atualizar estado global dos dados normalizados
      setDadosNormalizados(dadosNormalizadosTemp)

      setDadosExpedicao(dadosNormalizadosTemp)

      // Exibir mensagem apenas se não foi exibida ainda
      if (!messageShownRef.current && dadosNormalizadosTemp.length > 0) {
        messageShownRef.current = true
      } else if (dadosNormalizadosTemp.length === 0) {
        setDadosExpedicao([])
      }

    } catch (error) {
      console.error('Erro ao carregar dados de Expedição:', error)

      // Limpar dados em caso de erro
      setDadosExpedicao([])

      message.error('Erro ao carregar dados de Expedição: ' + (error.message || 'Erro desconhecido'))
    } finally {
      setLoading(false)
    }
  }

  const handleDateChange = (date) => {
    setSelectedDate(date)
    // Buscar dados automaticamente quando a data mudar
    if (date) {
      buscarDadosBackend(date)
    }
  }

  const handleRefresh = () => {
    // Buscar dados do backend ao invés de usar timeout
    buscarDadosBackend()
  }

  // Função para buscar dados de cubagem
  const buscarCubagem = async (dataFormatada) => {
    try {
      setCubagemLoading(true)
      // Buscar cubagem pela data (createdAt)
      const response = await notaMetragem.list({ date: dataFormatada, limit: 1000 })

      if (response && response.data) {
        // Extrair chaves dos dados de Expedição para filtrar
        const chavesExpedicao = new Set(dadosExpedicao.map(d => d.CHAVE || d.chave).filter(Boolean))

        // Filtrar cubagem que pertence às notas da Expedição do dia
        const cubagemFiltrada = response.data.filter(item => chavesExpedicao.has(item.chave))

        // Se houver dados de Expedição, filtramos por eles. Caso contrário, mostramos todos do dia (ou nada)
        // O pedido diz "conforme notas em dadosExpedicao", então vamos priorizar a filtragem se houver dados.
        const finalData = chavesExpedicao.size > 0 ? cubagemFiltrada : response.data;

        // Processar dados para o formato esperado pelo modelo
        const allItems = []
        const clientesUnicos = new Set()
        const notasUnicas = new Set()

        finalData.forEach(nm => {
          notasUnicas.add(nm.chave)
          // Tentar encontrar o cliente correspondente em dadosExpedicao
          const infoExp = dadosExpedicao.find(d => (d.CHAVE || d.chave) === nm.chave)
          const clienteNome = infoExp?.REMETENTE || infoExp?.CLIENTE || 'N/A'
          clientesUnicos.add(clienteNome)

          if (nm.itens && nm.itens.length > 0) {
            nm.itens.forEach(it => {
              allItems.push({
                ...it,
                id_nota_metragem: nm.id,
                chave: nm.chave,
                nota: infoExp?.NOTA || nm.chave.substring(25, 34),
                serie: infoExp?.SERIE || nm.chave.substring(22, 25),
                cliente_nome: clienteNome,
                cnpj_rmte: infoExp?.CNPJ_RMTE || '',
                metragem_total: it.total,
                largura_m: it.largura,
                comprimento_m: it.comprimento,
                altura_m: it.altura,
                qtd_volumes: it.quantidade
              })
            })
          }
        })

        setCubagemData({
          totalRegistros: allItems.length,
          totalClientes: clientesUnicos.size,
          totalNotas: notasUnicas.size,
          data: allItems
        })
      } else {
        setCubagemData({ totalRegistros: 0, totalClientes: 0, totalNotas: 0, data: [] })
      }
    } catch (error) {
      console.error('Erro ao buscar dados de cubagem:', error)
    } finally {
      setCubagemLoading(false)
    }
  }

  // Efeito para buscar cubagem quando os dados de Expedição ou data mudarem
  useEffect(() => {
    if (selectedDate) {
      buscarCubagem(selectedDate.format('YYYY-MM-DD'))
    }
  }, [selectedDate, dadosExpedicao])

  // Função para imprimir relatório de cubagem
  const handlePrintCubagem = (dadosParaImprimir) => {
    // Agrupar por nota para calcular totais por nota
    const notasAgrupadas = (dadosParaImprimir || []).reduce((acc, item) => {
      const key = `${item.nota}-${item.serie}-${item.cnpj_rmte || item.chave}`;
      if (!acc[key]) {
        acc[key] = {
          nota: item.nota,
          serie: item.serie,
          cliente: item.cliente_nome || item.cnpj_rmte || 'N/A',
          itens: [],
          totalM3: 0
        };
      }
      acc[key].itens.push(item);
      acc[key].totalM3 += Number(item.metragem_total) || 0;
      return acc;
    }, {});

    const notas = Object.values(notasAgrupadas);

    const printWindow = window.open('', '_blank');
    const html = `
      <html>
        <head>
          <title>Relatório de Cubagem - ${selectedDate ? selectedDate.format('DD/MM/YYYY') : dayjs().format('DD/MM/YYYY')}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; color: #333; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; margin-bottom: 30px; }
            th, td { border: 1px solid #000; padding: 6px; text-align: center; font-size: 11px; }
            th { background-color: #f2f2f2; font-weight: bold; }
            .cliente-header { text-align: left; background-color: #fff; font-weight: bold; border-bottom: 1px solid #000; }
            .total-row { font-weight: bold; background-color: #f9f9f9; }
            h1 { font-size: 18px; margin: 0; text-align: center; }
            .header { border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; display: flex; justify-content: space-between; align-items: flex-end; }
            @media print {
               @page { size: portrait; margin: 1cm; }
               button { display: none; }
             }
          </style>
        </head>
        <body>
          <div class="header">
            <div>
              <h1>Relatório de Cubagem</h1>
              <p style="margin: 5px 0 0 0;"><strong>Empresa:</strong> ${empresa}</p>
              <p style="margin: 2px 0 0 0;"><strong>Data:</strong> ${selectedDate ? selectedDate.format('DD/MM/YYYY') : dayjs().format('DD/MM/YYYY')}</p>
            </div>
            <div style="text-align: right;">
              <p style="margin: 0;"><strong>Usuário:</strong> ${user?.nome || user?.usuario || 'Sistema'}</p>
              <p style="margin: 2px 0 0 0;"><strong>Gerado em:</strong> ${new Date().toLocaleString('pt-BR')}</p>
            </div>
          </div>
          
          ${notas.map(notaGroup => `
            <table>
              <thead>
                <tr>
                  <th colspan="1" class="cliente-header">Cliente</th>
                  <th colspan="4" class="cliente-header" style="text-align: left;">${notaGroup.cliente}</th>
                </tr>
                <tr>
                  <th>Nota Fiscal</th>
                  <th>Qtde Volumes</th>
                  <th>Largura</th>
                  <th>Cumprimento</th>
                  <th>Altura</th>
                </tr>
              </thead>
              <tbody>
                ${notaGroup.itens.map(item => `
                  <tr>
                    <td>${item.nota}</td>
                    <td>${item.qtd_volumes}</td>
                    <td>${Number(item.largura_m).toFixed(2)}</td>
                    <td>${Number(item.comprimento_m).toFixed(2)}</td>
                    <td>${Number(item.altura_m).toFixed(2)}</td>
                  </tr>
                `).join('')}
                <tr class="total-row">
                  <td colspan="4" style="text-align: right;">Total da cubagem final dos itens da nota:</td>
                  <td>${notaGroup.totalM3.toFixed(4)} m³</td>
                </tr>
              </tbody>
            </table>
          `).join('')}

          <script>
            window.onload = () => {
              setTimeout(() => {
                window.print();
              }, 500);
            };
          </script>
        </body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
  };


  const handlePrintRelatorio = () => {
    // TODO: gerar/imprimir relatório gerencial de Expedição
  }

  const handleMobileMenuClick = () => {
    setSidebarVisible(!sidebarVisible)
  }

  useEffect(() => {
    buscarDadosBackend()
  }, [])

  const [activeTabKey, setActiveTabKey] = useState('timeline')
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const updateIsMobile = () => setIsMobile(window.innerWidth <= 768)
    updateIsMobile()
    window.addEventListener('resize', updateIsMobile)
    return () => window.removeEventListener('resize', updateIsMobile)
  }, [])

  // Função para filtrar dados onde inclusao = 'MANUAL'
  const getNotasManuais = () => {
    return dadosExpedicao.filter(item => item.inclusao === 'MANUAL')
  }

  // Função para agrupar dados por CARRO e contar ocorrências
  const getNotasManuaisAgrupadas = () => {
    const notasManuais = getNotasManuais()
    const agrupadas = {}

    notasManuais.forEach(item => {
      const carro = item.CARRO || 'SEM CARRO'
      if (agrupadas[carro]) {
        agrupadas[carro]++
      } else {
        agrupadas[carro] = 1
      }
    })

    return Object.entries(agrupadas).map(([carro, total]) => ({
      carro,
      total
    }))
  }

  // Função para abrir modal com detalhes do carro
  const abrirModalCarro = (carro) => {
    const notasDoCarro = dadosExpedicao.filter(item =>
      item.inclusao === 'MANUAL' && (item.CARRO || 'SEM CARRO') === carro
    )

    setCarroSelecionado(carro)
    setNotasCarroSelecionado(notasDoCarro)
    setModalVisible(true)
  }

  // Função para fechar modal
  const fecharModal = () => {
    setModalVisible(false)
    setCarroSelecionado(null)
    setNotasCarroSelecionado([])
  }

  // Função para filtrar notas que possuem alterações
  const getNotasAlteradas = () => {
    return dadosExpedicao.filter(item => {
      if (!item.alteracoes) return false

      try {
        // Se alteracoes é uma string JSON, fazer o parse

        const alteracoes = typeof item.alteracoes === 'string'
          ? JSON.parse(item.alteracoes)
          : item.alteracoes

        return Array.isArray(alteracoes) && alteracoes.length > 0
      } catch (error) {
        console.warn('Erro ao fazer parse das alterações:', error)
        return false
      }
    })
  }

  // Função para agrupar notas alteradas por CARRO e ACAO
  const getNotasAlteradasAgrupadas = () => {
    const notasAlteradas = getNotasAlteradas()
    const agrupadas = {}

    notasAlteradas.forEach(item => {
      const carro = item.CARRO || 'SEM CARRO'

      try {
        // Parse das alterações se for string JSON
        const alteracoes = typeof item.alteracoes === 'string'
          ? JSON.parse(item.alteracoes)
          : item.alteracoes

        // Para cada nota, pegar o último registro de cada tipo de ação
        if (alteracoes && Array.isArray(alteracoes)) {
          const acoesUnicas = {}

          // Agrupar por ação e pegar o mais recente
          alteracoes.forEach(alteracao => {
            const acao = alteracao.acao || 'SEM_ACAO'
            if (!acoesUnicas[acao] || new Date(alteracao.data) > new Date(acoesUnicas[acao].data)) {
              acoesUnicas[acao] = alteracao
            }
          })

          // Contar as ações únicas para este carro
          Object.keys(acoesUnicas).forEach(acao => {
            const chave = `${carro}_${acao}`
            if (agrupadas[chave]) {
              agrupadas[chave].total++
            } else {
              agrupadas[chave] = {
                carro,
                acao,
                total: 1
              }
            }
          })
        }
      } catch (error) {
        console.warn('Erro ao processar alterações para agrupamento:', error)
      }
    })

    return Object.values(agrupadas)
  }

  // Função para abrir modal com detalhes das notas alteradas
  const abrirModalCarroAlteracoes = (carro, acao = null) => {
    const notasDoCarroAlteradas = dadosExpedicao.filter(item => {
      const carroMatch = (item.CARRO || 'SEM CARRO') === carro

      if (!carroMatch || !item.alteracoes) return false

      try {
        // Parse das alterações se for string JSON
        const alteracoes = typeof item.alteracoes === 'string'
          ? JSON.parse(item.alteracoes)
          : item.alteracoes

        const temAlteracoes = Array.isArray(alteracoes) && alteracoes.length > 0

        if (!temAlteracoes) return false

        // Se uma ação específica foi fornecida, filtrar por ela
        if (acao && acao !== 'SEM_ACAO') {
          return alteracoes.some(alt => alt.acao === acao)
        }

        return true
      } catch (error) {
        console.warn('Erro ao processar alterações no modal:', error)
        return false
      }
    })

    setCarroSelecionado(carro)
    setNotasCarroSelecionado(notasDoCarroAlteradas)
    setModalVisible(true)
  }

  // Função para abrir modal de lotes
  const abrirModalLotes = (carro) => {
    // Filtrar dados normalizados pelo carro selecionado
    const dadosDoCarro = dadosNormalizados.filter(item =>
      (item.CARRO || 'SEM CARRO') === carro
    )

    setCarroSelecionadoLotes(carro)
    setDadosCarroLotes(dadosDoCarro)
    setModalLotesVisible(true)

    // Persistir estado da ModalLotes para restaurar ao voltar
    try {
      const saved = {
        isOpen: true,
        carro,
        dadosCarroLotes: dadosDoCarro,
        selectedDate: selectedDate ? selectedDate.format('YYYY-MM-DD') : ''
      }
      sessionStorage.setItem('modalLotesState', JSON.stringify(saved))
    } catch (e) {
      // Ignorar erros de storage
    }
  }

  // Função para fechar modal de lotes
  const fecharModalLotes = () => {
    // Apenas ocultar a modal; manter dados carregados
    setModalLotesVisible(false)

    // Atualizar storage para refletir que a modal foi fechada, sem perder dados
    try {
      const prev = sessionStorage.getItem('modalLotesState')
      const payload = prev ? JSON.parse(prev) : {}
      sessionStorage.setItem('modalLotesState', JSON.stringify({
        ...payload,
        isOpen: false
      }))
    } catch (e) {
      // Ignorar erros de storage
    }
    fetchLotesDia()
  }


  // Função para renderizar a timeline
  const renderTimeline = (data) => {

    const parseDate = (dateString) => {
      if (!dateString) return 'xx:xx'
      const str = String(dateString)
      const delimIndex = Math.max(str.indexOf('T'), str.indexOf(' '))
      const start = delimIndex >= 0 ? delimIndex + 1 : 0
      const slice = str.slice(start)
      const m = slice.match(/^([0-9]{2}):([0-9]{2})/)
      if (m) return `${m[1]}:${m[2]}`
      const any = str.match(/([0-9]{2}):([0-9]{2})/)
      return any ? any[0] : 'xx:xx'
    }

    const timelineContainer = document.getElementById('timeline-container');
    if (!timelineContainer) return;

    timelineContainer.innerHTML = '';

    if (!data || data.length === 0) {
      console.warn('Nenhum dado disponível para a timeline');
      timelineContainer.innerHTML = '<div class="alert alert-info">Nenhum dado disponível para a timeline.</div>';
      return;
    }

    // Agrupar dados por CARRO
    const carroGroups = {};
    data.forEach(item => {
      const carro = item.CARRO || 'SEM CARRO';
      if (!carroGroups[carro]) {
        carroGroups[carro] = [];
      }
      carroGroups[carro].push(item);
    });

    // Renderizar timeline para cada carro com ordenação de finalizados primeiro
    Object.entries(carroGroups)
      .sort((a, b) => {
        const aFinal = a[1].some(i => Number(i.STATUS_COLETA) === 1)
        const bFinal = b[1].some(i => Number(i.STATUS_COLETA) === 1)
        return Number(bFinal) - Number(aFinal)
      })
      .forEach(([carro, items]) => {
        const carDiv = document.createElement('div');
        carDiv.className = 'car-timeline';

        // Título do carro
        const carTitle = document.createElement('div');
        carTitle.className = 'car-title';
        carTitle.textContent = carro;
        carTitle.style.cursor = 'pointer';
        carTitle.title = 'Clique para abrir gestão de lotes';

        // Adicionar evento de click para abrir modal de lotes
        carTitle.addEventListener('click', () => {
          abrirModalLotes(carro);
        });

        // Verificar se há notas finalizadas para piscar
        const hasFinalized = items.some(item => Number(item.STATUS_COLETA) === 1);
        if (hasFinalized) {
          carTitle.classList.add('blink-bg');
        }

        carDiv.appendChild(carTitle);

        // Contagem de lotes e notas do carro
        const lotesNotasCount = document.createElement('div');
        lotesNotasCount.className = 'car-lotes-notas-count';

        // Calcular quantidade de notas em lotes (lotes_relatorio_itens) do carro no dia
        const lotesDoCarro = Array.isArray(lotesPorCarro[carro]) ? lotesPorCarro[carro] : [];
        const totalLotes = lotesDoCarro.reduce((sum, l) => sum + (Array.isArray(l.itens) ? l.itens.length : 0), 0);

        // Contar a quantidade de notas do carro (cada item é uma nota)
        const totalNotas = items.length;

        // Exibir no formato: lotes / notas
        lotesNotasCount.textContent = `Notas em Lotes: ${totalLotes} / ${totalNotas}`;
        lotesNotasCount.style.fontSize = '10px';
        lotesNotasCount.style.color = '#666';
        lotesNotasCount.style.fontWeight = 'normal';
        lotesNotasCount.style.marginTop = '2px';
        lotesNotasCount.style.textAlign = 'left';
        lotesNotasCount.title = `${totalLotes} lote${totalLotes !== 1 ? 's' : ''} / ${totalNotas} nota${totalNotas !== 1 ? 's' : ''}`;

        carDiv.appendChild(lotesNotasCount);

        // Container da timeline
        const timelineWrapper = document.createElement('div');
        timelineWrapper.className = 'timeline-wrapper';

        // Agrupar por cliente
        const clientGroups = {};
        items.forEach(item => {
          const cliente = item.REMETENTE || 'SEM CLIENTE';
          if (!clientGroups[cliente]) {
            clientGroups[cliente] = [];
          }
          clientGroups[cliente].push(item);
        });

        // 1. Container dos nomes dos clientes (acima da linha)
        const clientsNamesContainer = document.createElement('div');
        clientsNamesContainer.className = 'clients-container';

        // 2. Linha da timeline com indicadores
        const timelineLine = document.createElement('div');
        timelineLine.className = 'timeline-line';

        // 3. Container dos horários dos clientes (abaixo da linha)
        const clientsTimesContainer = document.createElement('div');
        clientsTimesContainer.className = 'clients-container';

        // Renderizar cada cliente
        let clientIndex = 0;
        const totalClients = Object.keys(clientGroups).length;
        let maxLeft = 0;

        Object.entries(clientGroups)
          .sort((a, b) => {
            const aFinal = a[1].some(i => Number(i.STATUS_COLETA) === 1)
            const bFinal = b[1].some(i => Number(i.STATUS_COLETA) === 1)
            if (aFinal !== bFinal) return Number(bFinal) - Number(aFinal)
            return String(a[0]).localeCompare(String(b[0]))
          })
          .forEach(([cliente, clientItems], index) => {
            // 1. Criar div do nome do cliente (acima da linha)
            const clientNameDiv = document.createElement('div');
            clientNameDiv.className = 'client-item';

            const clientName = document.createElement('div');
            clientName.className = 'client-name';
            clientName.textContent = cliente;

            clientNameDiv.appendChild(clientName);
            clientsNamesContainer.appendChild(clientNameDiv);

            // 2. Criar indicador na linha
            const indicator = document.createElement('div');
            indicator.className = 'timeline-indicator';
            const leftPosition = 50 + (clientIndex * 110); // Espaçamento de 110px entre indicadores
            if (leftPosition > maxLeft) maxLeft = leftPosition;

            // Lógica para determinar o ícone baseado na posição e status
            if (index === totalClients - 1) {
              // Último cliente
              // Verificar coleta finalizada 
              const hasFinalized = clientItems.some(item => Number(item.STATUS_COLETA) === 1);
              if (hasFinalized) {
                indicator.classList.add('truck-indicator');
                indicator.innerHTML = '🏆';
              } else {
                // Último cliente: emoji de caminhão 
                indicator.classList.add('truck-indicator');
                indicator.innerHTML = '⛟';
              }
            } else {
              // Outros clientes: emoji de check verde 
              indicator.classList.add('check-indicator');
              indicator.innerHTML = '✓';
            }

            indicator.style.left = `${leftPosition}px`;
            timelineLine.appendChild(indicator);

            // 3. Criar div dos horários do cliente (abaixo da linha)
            const clientTimesDiv = document.createElement('div');
            clientTimesDiv.className = 'client-item';

            const clientTimes = document.createElement('div');
            clientTimes.className = 'client-times';

            // Encontrar horários mínimo e máximo usando PRIMEIRA_ETIQUETA e ULTIMA_ETIQUETA
            const primeiraEtiqueta = clientItems
              .map(item => item.PRIMEIRA_ETIQUETA)
              .filter(time => time)
              .sort()[0]; // Pega a primeira (mais antiga)

            const ultimaEtiqueta = clientItems
              .map(item => item.ULTIMA_ETIQUETA)
              .filter(time => time)
              .sort()
              .reverse()[0]; // Pega a última (mais recente)


            const primeiraHora = parseDate(primeiraEtiqueta);
            const ultimaHora = (typeof ultimaEtiqueta === 'undefined' || !ultimaEtiqueta) ? 'xx:xx' : parseDate(ultimaEtiqueta);
            clientTimes.innerHTML = `<span class="time-range">${primeiraHora} - ${ultimaHora}</span>`;

            clientTimesDiv.appendChild(clientTimes);
            clientsTimesContainer.appendChild(clientTimesDiv);

            clientIndex++;
          });

        // Adicionar bandeira de chegada ao final da linha
        const finishFlag = document.createElement('div');
        finishFlag.className = 'timeline-finish-flag';
        finishFlag.innerHTML = '🏁';
        // Posiciona no final exato da linha (100%)
        finishFlag.style.left = '100%';
        timelineLine.appendChild(finishFlag);

        const requiredWidth = Math.max(300, maxLeft + 120);
        timelineLine.style.width = '100%';
        clientsNamesContainer.style.width = `${requiredWidth}px`;
        clientsTimesContainer.style.width = `${requiredWidth}px`;

        // Montar a estrutura na ordem correta
        timelineWrapper.appendChild(clientsNamesContainer);  // Nomes acima
        timelineWrapper.appendChild(timelineLine);           // Linha no meio
        timelineWrapper.appendChild(clientsTimesContainer);  // Horários abaixo

        carDiv.appendChild(timelineWrapper);
        timelineContainer.appendChild(carDiv);
      });
  };

  // useEffect para renderizar timeline quando dados mudarem
  useEffect(() => {
    if (dadosExpedicao.length > 0) {
      // Pequeno delay para garantir que o DOM foi atualizado
      setTimeout(() => {
        renderTimeline(dadosExpedicao);
      }, 100);
    }
  }, [dadosExpedicao]);

  // Re-renderizar timeline quando os lotes do dia por carro forem atualizados
  useEffect(() => {
    if (dadosExpedicao.length > 0) {
      setTimeout(() => {
        renderTimeline(dadosExpedicao)
      }, 100)
    }
  }, [lotesPorCarro])

  const timelineCard = (
    <Card
      title="Timeline"
      size="small"
      style={{
        marginTop: '0',
        height: isMobile ? 'auto' : 'calc(100vh - 240px)',
        width: '100%',
        maxWidth: '100%',
        backgroundColor: currentTheme === 'dark' ? '#1f1f1f' : undefined,
        borderColor: currentTheme === 'dark' ? '#434343' : undefined,
      }}
      styles={{
        header: {
          backgroundColor: currentTheme === 'dark' ? '#1f1f1f' : undefined,
          borderBottomColor: currentTheme === 'dark' ? '#434343' : undefined,
          color: currentTheme === 'dark' ? '#ffffff' : undefined,
          padding: '0 16px',
        },
        body: {
          backgroundColor: currentTheme === 'dark' ? '#1f1f1f' : undefined,
          height: isMobile ? 'auto' : 'calc(100% - 40px)',
          padding: '0',
          overflow: 'hidden'
        }
      }}
    >
      <div className="timeline-scroll-wrapper" data-theme={currentTheme}>
        <div id="timeline-container">
        </div>
      </div>
    </Card>
  )
  const renderLoteItemsTable = (carro, lote) => {
    const rows = (Array.isArray(lote?.itens) ? lote.itens : []).map((item) => ({
      key: `it-${carro}-${lote.id}-${item.seq || item.nota || item.chave || Math.random()}`,
      nota: item.nota || item.NOTA || 'N/A',
      nr1: item.nr1 || 'N/A',
      nr2: item.nr2 || 'N/A',
      destinatario: item.destinatario || item.DESTINATARIO || 'N/A',
      cidade: item.cidade || item.CIDADE || 'N/A',
      uf: item.uf || item.UF || 'N/A',
      volumes: item.volumes || item.VOLUMES || item.vol || 'N/A',
      chave_nfe: item.chave_nfe || item.chave || item.CODIGO || 'N/A'
    }))
    const columns = [
      { title: 'Nota', dataIndex: 'nota', key: 'nota', width: 100, render: (text) => <span style={{ fontSize: '10px' }}>{text || 'N/A'}</span> },
      { title: 'NR1', dataIndex: 'nr1', key: 'nr1', width: 80, render: (text) => <span style={{ fontSize: '10px' }}>{text || 'N/A'}</span> },
      { title: 'NR2', dataIndex: 'nr2', key: 'nr2', width: 80, render: (text) => <span style={{ fontSize: '10px' }}>{text || 'N/A'}</span> },
      { title: 'Destinatário', dataIndex: 'destinatario', key: 'destinatario', width: 200, render: (text) => <span style={{ fontSize: '10px' }}>{text || 'N/A'}</span> },
      { title: 'Cidade', dataIndex: 'cidade', key: 'cidade', width: 140, render: (text) => <span style={{ fontSize: '10px' }}>{text || 'N/A'}</span> },
      { title: 'UF', dataIndex: 'uf', key: 'uf', width: 50, render: (text) => <span style={{ fontSize: '10px' }}>{text || 'N/A'}</span> },
      { title: 'Vol(s)', dataIndex: 'volumes', key: 'volumes', width: 80, render: (text) => <span style={{ fontSize: '10px' }}>{text || 'N/A'}</span> },
      { title: 'Chave', dataIndex: 'chave_nfe', key: 'chave_nfe', width: 220, render: (text) => <span style={{ fontSize: '10px', color: '#999' }}>{text || 'N/A'}</span> }
    ]
    return (
      <Table size="small" pagination={false} scroll={{ x: 1060, y: 260 }} columns={columns} dataSource={rows} />
    )
  }

  const lotesCard = (
    <Card
      title="Lotes do Dia"
      size="small"
      style={{ marginTop: '0', width: '100%', backgroundColor: currentTheme === 'dark' ? '#1f1f1f' : undefined, borderColor: currentTheme === 'dark' ? '#434343' : undefined }}
      styles={{
        header: { backgroundColor: currentTheme === 'dark' ? '#1f1f1f' : undefined, borderBottomColor: currentTheme === 'dark' ? '#434343' : undefined, color: currentTheme === 'dark' ? '#ffffff' : undefined, padding: '0 16px' },
        body: { backgroundColor: currentTheme === 'dark' ? '#1f1f1f' : undefined }
      }}
    >
      <div style={{ width: '100%' }}>
        <Collapse size="small">
          {Object.entries(lotesPorCarro).map(([carro, lotes]) => (
            <Collapse.Panel key={`car-${carro}`} header={`${carro} (${Array.isArray(lotes) ? lotes.length : 0} lote${(lotes || []).length !== 1 ? 's' : ''})`}>
              <Collapse size="small">
                {(Array.isArray(lotes) ? lotes : []).map((l) => (
                  <Collapse.Panel key={`lote-${carro}-${l.id}`} header={`${l.descricao || '—'} • ${l.usuario || '—'} • ${(l.data_criacao || '').toString().slice(0, 10)}`}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
                      <Button size="small" icon={<PrinterOutlined />} onClick={() => handleAbrirRelatorioLote(carro, l)}>Abrir relatório</Button>
                    </div>
                    {renderLoteItemsTable(carro, l)}
                  </Collapse.Panel>
                ))}
              </Collapse>
            </Collapse.Panel>
          ))}
        </Collapse>
      </div>
    </Card>
  )

  const conferenciaCard = (
    <ConferenciaExpedicaoTab
      dadosExpedicao={dadosExpedicao}
      selectedDate={selectedDate}
      empresa={empresa}
      dominio={user?.organization?.dominio || user?.organization?.domain || user?.erpDominio}
      currentTheme={currentTheme}
      isMobile={isMobile}
    />
  )

  const tabsItems = [
    { key: 'timeline', label: 'Timeline', children: timelineCard },
    { key: 'lotes', label: 'Lotes', children: lotesCard },
    { key: 'conferencia', label: 'Conferência', children: conferenciaCard }
  ]


  const fetchLotesDia = useCallback(async () => {
    try {
      setLoadingLotes(true)
      const dia = selectedDate ? selectedDate.format('YYYY-MM-DD') : ''
      const carros = Array.from(new Set((dadosNormalizados || []).map(it => (it.CARRO || 'SEM CARRO')).filter(Boolean)))
      const results = await Promise.all(carros.map(async (car) => {
        try {
          const resp = await lotesRelatorio.buscarPorCarro({ carro: String(car), empresa: String(empresa || ''), data_criacao: String(dia || '') })
          const arr = Array.isArray(resp) ? resp : Array.isArray(resp?.data) ? resp.data : Array.isArray(resp?.result) ? resp.result : (resp && typeof resp === 'object' ? [resp] : [])
          const mapa = new Map()
          arr.forEach(it => {
            const id = String(it?.lote_id || it?.lote || it?.id || it?.descricao || 'Lote')
            const atual = mapa.get(id) || { id, descricao: it?.observacoes || it?.descricao || '—', usuario: it?.usuario || '', data_criacao: it?.data_criacao || '', itens: [] }
            const itensDoLote = Array.isArray(it?.itens) ? it.itens : []
            if (itensDoLote.length > 0) {
              atual.itens.push(...itensDoLote)
            }
            mapa.set(id, atual)
          })
          const lotes = Array.from(mapa.values()).sort((a, b) => (b.itens.length || 0) - (a.itens.length || 0))
          return [car, lotes]
        } catch {
          return [car, []]
        }
      }))
      const obj = {}
      results.forEach(([car, lotes]) => { obj[car] = lotes })
      setLotesPorCarro(obj)
    } finally {
      setLoadingLotes(false)
    }
  }, [dadosNormalizados, selectedDate, empresa])

  useEffect(() => {
    if ((dadosNormalizados || []).length > 0) {
      fetchLotesDia()
    }
  }, [dadosNormalizados, selectedDate, empresa, fetchLotesDia])


  const handleAbrirRelatorioLote = (carro, lote) => {
    try {
      const loteId = String(lote?.id || lote?.lote_id || '')
      const meta = {
        lote_id: loteId,
        data_criacao: (lote?.data_criacao || selectedDate?.format('YYYY-MM-DD') || ''),
        descricao: (lote?.descricao || ''),
        carro: String(carro || ''),
        empresa: String(empresa || ''),
        usuario: user?.nome || user?.username || 'Usuário'
      }

      // Mapear itens para garantir que campos necessários como cnpj e remetente estejam presentes
      const itens = (Array.isArray(lote?.itens) ? lote.itens : []).map(it => ({
        ...it,
        cnpj: it?.cnpj || it?.CNPJ || it?.cnpj_remetente || it?.CNPJ_REMETENTE || '',
        remetente: it?.remetente || it?.REMETENTE || it?.remetente_nome || it?.REMETENTE_NOME || ''
      }))

      abrirRelatorioLote({ loteId, meta, itens, user })
    } catch (e) {
      console.error('Erro ao abrir relatório de lote:', e)
    }
  }

  // Colunas para a tabela de cubagem no modal
  const columnsCubagem = [
    {
      title: 'Cliente',
      dataIndex: 'cliente_nome',
      key: 'cliente_nome',
      width: 200,
      sorter: (a, b) => a.cliente_nome.localeCompare(b.cliente_nome),
    },
    {
      title: 'Nota',
      dataIndex: 'nota',
      key: 'nota',
      width: 100,
      render: (text, record) => `${text}${record.serie ? `/${record.serie}` : ''}`,
      sorter: (a, b) => a.nota.localeCompare(b.nota),
    },
    {
      title: 'Volumes',
      dataIndex: 'qtd_volumes',
      key: 'qtd_volumes',
      width: 80,
    },
    {
      title: 'Dimensões (L x C x A)',
      key: 'dimensoes',
      width: 180,
      render: (_, record) => `${Number(record.largura_m).toFixed(2)} x ${Number(record.comprimento_m).toFixed(2)} x ${Number(record.altura_m).toFixed(2)}`
    },
    {
      title: 'Total (m³)',
      dataIndex: 'metragem_total',
      key: 'metragem_total',
      width: 100,
      render: (val) => Number(val).toFixed(4),
      sorter: (a, b) => Number(a.metragem_total) - Number(b.metragem_total),
    }
  ]

  // Filtrar dados de cubagem para exibição no modal
  const getFilteredCubagemData = () => {
    return (cubagemData.data || []).filter(item => {
      const matchCliente = !cubagemFilters.cliente ||
        (item.cliente_nome && item.cliente_nome.toLowerCase().includes(cubagemFilters.cliente.toLowerCase()));
      const matchNota = !cubagemFilters.nota ||
        (item.nota && item.nota.includes(cubagemFilters.nota)) ||
        (item.chave && item.chave.includes(cubagemFilters.nota));
      return matchCliente && matchNota;
    });
  }

  const coreContent = (
    <>
      <div
        className="content-header"
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: isMobile ? 'flex-start' : 'space-between',
          alignItems: isMobile ? 'flex-start' : 'center',
          gap: isMobile ? 8 : 0,
          padding: isMobile ? '8px' : '0 16px 12px 8px',
          borderBottom: currentTheme === 'dark' ? '1px solid #333333' : '1px solid #f0f0f0',
          backgroundColor: currentTheme === 'dark' ? '#111111' : undefined,
        }}
      >
        <Title level={2} style={{ margin: isMobile ? '0 0 8px 0' : 0, color: currentTheme === 'dark' ? '#ffffff' : undefined }}>
          Painel Expedição
        </Title>

        <Space size={isMobile ? 6 : 'middle'} style={{ alignItems: 'center', flexWrap: isMobile ? 'nowrap' : undefined }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span
              className="empresa-label"
              style={{ fontSize: isMobile ? '12px' : '14px', color: currentTheme === 'dark' ? '#ffffff' : undefined }}
            >
              Empresa:
            </span>
            <Input
              name="txt-empresa"
              readOnly
              value={empresa}
              onChange={(e) => setEmpresa(e.target.value)}
              style={{
                width: isMobile ? '90px' : '80px',
                backgroundColor: currentTheme === 'dark' ? '#1e293b !important' : undefined,
                borderColor: currentTheme === 'dark' ? '#475569 !important' : undefined,
                color: currentTheme === 'dark' ? '#ffffff !important' : undefined,
              }}
              styles={{
                input: {
                  color: currentTheme === 'dark' ? '#ffffff !important' : undefined,
                  backgroundColor: currentTheme === 'dark' ? '#1e293b !important' : undefined,
                },
              }}
              size="small"
            />
          </div>

          <DatePicker
            value={selectedDate}
            onChange={handleDateChange}
            placeholder="Selecionar data"
            size="small"
            style={{
              width: isMobile ? '130px' : '140px',
              backgroundColor: currentTheme === 'dark' ? '#1e293b !important' : undefined,
              borderColor: currentTheme === 'dark' ? '#475569 !important' : undefined,
              color: currentTheme === 'dark' ? '#ffffff !important' : undefined,
            }}
            styles={{
              input: {
                color: currentTheme === 'dark' ? '#ffffff !important' : undefined,
                backgroundColor: currentTheme === 'dark' ? '#1e293b !important' : undefined,
              },
            }}
          />

          <Button
            type="primary"
            icon={<ClockCircleOutlined />}
            onClick={handleRefresh}
            loading={loading}
            size="small"
            style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: isMobile ? '12px' : '12px', padding: isMobile ? '0 8px' : undefined, minWidth: isMobile ? 'auto' : undefined, whiteSpace: 'nowrap' }}
          >
            {isMobile ? 'Atualizar' : `${currentTime} Atualizar`}
          </Button>

          <Button id="relatorio-gerencial" icon={<PrinterOutlined />} onClick={handlePrintRelatorio} size="small" style={{ display: 'flex', alignItems: 'center', width: isMobile ? 28 : undefined, height: isMobile ? 28 : undefined, padding: isMobile ? 0 : undefined }} />
        </Space>
      </div>

      <Row gutter={[16, 16]} style={{ margin: '16px 8px', width: '100%' }}>
        <Col xs={24} md={8}>
          <Card
            title="Notas Manuais"
            size="small"
            style={{
              width: '100%',
              minWidth: 0,
              backgroundColor: currentTheme === 'dark' ? '#1f1f1f' : undefined,
              borderColor: currentTheme === 'dark' ? '#333333' : undefined,
            }}
            styles={{
              header: {
                backgroundColor: currentTheme === 'dark' ? '#111111' : undefined,
                borderBottom: currentTheme === 'dark' ? '1px solid #333333' : undefined,
                color: currentTheme === 'dark' ? '#ffffff' : undefined,
              },
              body: {
                backgroundColor: currentTheme === 'dark' ? '#1f1f1f' : undefined,
                padding: '12px',
              }
            }}
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
              {getNotasManuaisAgrupadas().length > 0 ? (
                getNotasManuaisAgrupadas().map(({ carro, total }) => (
                  <Badge
                    key={carro}
                    count={total}
                    style={{
                      backgroundColor: '#52c41a',
                      color: '#fff',
                      fontWeight: 'bold',
                    }}
                    showZero
                  >
                    <div
                      onClick={() => abrirModalCarro(carro)}
                      style={{
                        padding: '4px 12px',
                        backgroundColor: currentTheme === 'dark' ? '#2f2f2f' : '#f5f5f5',
                        border: currentTheme === 'dark' ? '1px solid #444444' : '1px solid #d9d9d9',
                        borderRadius: '6px',
                        color: currentTheme === 'dark' ? '#ffffff' : '#333333',
                        fontSize: '12px',
                        fontWeight: '500',
                        minWidth: '60px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = currentTheme === 'dark' ? '#404040' : '#e6f7ff'
                        e.target.style.borderColor = '#1890ff'
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = currentTheme === 'dark' ? '#2f2f2f' : '#f5f5f5'
                        e.target.style.borderColor = currentTheme === 'dark' ? '#444444' : '#d9d9d9'
                      }}
                    >
                      {carro}
                    </div>
                  </Badge>
                ))
              ) : (
                <Text
                  style={{
                    color: currentTheme === 'dark' ? '#888888' : '#999999',
                    fontSize: '12px',
                  }}
                >
                  Nenhuma nota manual encontrada
                </Text>
              )}
            </div>
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card
            title="Notas Alteradas"
            size="small"
            style={{
              width: '100%',
              minWidth: 0,
              backgroundColor: currentTheme === 'dark' ? '#1f1f1f' : undefined,
              borderColor: currentTheme === 'dark' ? '#333333' : undefined,
            }}
            styles={{
              header: {
                backgroundColor: currentTheme === 'dark' ? '#111111' : undefined,
                borderBottom: currentTheme === 'dark' ? '1px solid #333333' : undefined,
                color: currentTheme === 'dark' ? '#ffffff' : undefined,
              },
              body: {
                backgroundColor: currentTheme === 'dark' ? '#1f1f1f' : undefined,
                padding: '12px',
              }
            }}
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
              {getNotasAlteradasAgrupadas().length > 0 ? (
                getNotasAlteradasAgrupadas().map(({ carro, acao, total }) => (
                  <Badge
                    key={`${carro}_${acao}`}
                    count={total}
                    style={{
                      backgroundColor: '#fa8c16',
                      color: '#fff',
                      fontWeight: 'bold',
                    }}
                    showZero
                  >
                    <div
                      onClick={() => abrirModalCarroAlteracoes(carro, acao)}
                      style={{
                        padding: '4px 12px',
                        backgroundColor: currentTheme === 'dark' ? '#2f2f2f' : '#f5f5f5',
                        border: currentTheme === 'dark' ? '1px solid #444444' : '1px solid #d9d9d9',
                        borderRadius: '6px',
                        color: currentTheme === 'dark' ? '#ffffff' : '#333333',
                        fontSize: '12px',
                        fontWeight: '500',
                        minWidth: '60px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = currentTheme === 'dark' ? '#404040' : '#e6f7ff'
                        e.target.style.borderColor = '#fa8c16'
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = currentTheme === 'dark' ? '#2f2f2f' : '#f5f5f5'
                        e.target.style.borderColor = currentTheme === 'dark' ? '#444444' : '#d9d9d9'
                      }}
                    >
                      {carro}
                      {acao && acao !== 'SEM_ACAO' && (
                        <div style={{ fontSize: '10px', opacity: 0.8 }}>
                          {acao}
                        </div>
                      )}
                    </div>
                  </Badge>
                ))
              ) : (
                <Text
                  style={{
                    color: currentTheme === 'dark' ? '#888888' : '#999999',
                    fontSize: '12px',
                  }}
                >
                  Nenhuma nota alterada encontrada para a data selecionada
                </Text>
              )}
            </div>
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card
            title="Cubagem"
            size="small"
            style={{
              width: '100%',
              minWidth: 0,
              backgroundColor: currentTheme === 'dark' ? '#1f1f1f' : undefined,
              borderColor: currentTheme === 'dark' ? '#333333' : undefined,
            }}
            styles={{
              header: {
                backgroundColor: currentTheme === 'dark' ? '#111111' : undefined,
                borderBottom: currentTheme === 'dark' ? '1px solid #333333' : undefined,
                color: currentTheme === 'dark' ? '#ffffff' : undefined,
              },
              body: {
                backgroundColor: currentTheme === 'dark' ? '#1f1f1f' : undefined,
                padding: '12px',
              }
            }}
          >
            <div style={{
              display: 'flex',
              overflowX: 'auto',
              gap: '12px',
              alignItems: 'center',
              paddingTop: '10px',
              paddingBottom: '8px',
              scrollbarWidth: 'thin',
              msOverflowStyle: 'auto'
            }}>
              {cubagemData.totalRegistros > 0 ? (
                <Badge
                  count={`${cubagemData.totalClientes} Clientes / ${cubagemData.totalNotas} Notas`}
                  style={{
                    backgroundColor: '#1890ff',
                    color: '#fff',
                    fontWeight: 'bold',
                    flexShrink: 0,
                    padding: '0 8px',
                    borderRadius: '10px',
                    boxShadow: '0 0 0 1px #fff'
                  }}
                  showZero
                  offset={[10, 0]}
                >
                  <div
                    onClick={() => setCubagemModalVisible(true)}
                    style={{
                      padding: '6px 16px',
                      backgroundColor: currentTheme === 'dark' ? '#2f2f2f' : '#f5f5f5',
                      border: currentTheme === 'dark' ? '1px solid #444444' : '1px solid #d9d9d9',
                      borderRadius: '6px',
                      color: currentTheme === 'dark' ? '#ffffff' : '#333333',
                      fontSize: '13px',
                      fontWeight: '600',
                      minWidth: '120px',
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = currentTheme === 'dark' ? '#404040' : '#e6f7ff'
                      e.target.style.borderColor = '#1890ff'
                      e.target.style.transform = 'translateY(-1px)'
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = currentTheme === 'dark' ? '#2f2f2f' : '#f5f5f5'
                      e.target.style.borderColor = currentTheme === 'dark' ? '#444444' : '#d9d9d9'
                      e.target.style.transform = 'translateY(0)'
                    }}
                  >
                    Ver Detalhes Cubagem
                  </div>
                </Badge>
              ) : (
                <Text
                  style={{
                    color: currentTheme === 'dark' ? '#888888' : '#999999',
                    fontStyle: 'italic',
                    fontSize: '12px'
                  }}
                >
                  {cubagemLoading ? 'Buscando dados de cubagem...' : 'Nenhuma cubagem realizada'}
                </Text>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      <div style={{ width: '100%', margin: isMobile ? '8px' : '8px 8px 0', overflowX: 'hidden', minWidth: 0 }}>
        <Tabs
          tabPosition={isMobile ? 'top' : 'left'}
          size="small"
          items={tabsItems}
          style={{ paddingLeft: isMobile ? '0px' : '0px', paddingRight: isMobile ? '8px' : '24px' }}
          activeKey={activeTabKey}
          onChange={(key) => {
            setActiveTabKey(key)
            if (key === 'timeline' && dadosExpedicao.length > 0) {
              setTimeout(() => {
                renderTimeline(dadosExpedicao)
              }, 0)
            }
          }}
        />
      </div>

      {/* Notas Manuais e Alteradas */}
      <Modal
        title={`Carro: ${carroSelecionado}`}
        open={modalVisible}
        onCancel={fecharModal}
        footer={[
          <Button key="fechar" onClick={fecharModal}>
            Fechar
          </Button>
        ]}
        width={1000}
        style={{
          top: 20,
        }}
        styles={{
          body: {
            backgroundColor: currentTheme === 'dark' ? '#1f1f1f' : undefined,
            maxHeight: '70vh',
            overflowY: 'auto',
          }
        }}
      >
        <Table
          dataSource={notasCarroSelecionado}
          rowKey={(record) => `${record.NOTA}-${record.SERIE}-${record.CNPJ}`}
          size="small"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} notas`,
          }}
          scroll={{ x: 1200 }}
          style={{
            backgroundColor: currentTheme === 'dark' ? '#1f1f1f' : undefined,
          }}
          columns={[
            {
              title: 'Nota',
              dataIndex: 'NOTA',
              key: 'NOTA',
              width: 100,
              sorter: (a, b) => a.NOTA - b.NOTA,
            },
            {
              title: 'Série',
              dataIndex: 'SERIE',
              key: 'SERIE',
              width: 80,
            },
            {
              title: 'Chave',
              dataIndex: 'chave',
              key: 'chave',
              width: 150,
              ellipsis: true,
              render: (text) => text || '-',
            },
            {
              title: 'Remetente',
              dataIndex: 'REMETENTE',
              key: 'REMETENTE',
              width: 200,
              ellipsis: true,
            },
            {
              title: 'Destinatário',
              dataIndex: 'destinatario',
              key: 'destinatario',
              width: 200,
              ellipsis: true,
              render: (text, record) => {
                // Mostrar destinatário apenas para registros manuais
                return record.inclusao === 'MANUAL' ? (text || '-') : '-'
              },
            },
            {
              title: 'Volumes',
              dataIndex: 'VOLUMES',
              key: 'VOLUMES',
              width: 80,
              align: 'center',
              sorter: (a, b) => a.VOLUMES - b.VOLUMES,
            },
            {
              title: 'Peso (kg)',
              dataIndex: 'PESO',
              key: 'PESO',
              width: 90,
              align: 'center',
              render: (text, record) => {
                // Mostrar peso apenas para registros manuais
                return record.inclusao === 'MANUAL' ? (text || 0) : '-'
              },
              sorter: (a, b) => (a.PESO || 0) - (b.PESO || 0),
            },
            {
              title: 'M³',
              dataIndex: 'M3',
              key: 'M3',
              width: 80,
              align: 'center',
              render: (text, record) => {
                // Mostrar M3 apenas para registros manuais
                return record.inclusao === 'MANUAL' ? (text || 0) : '-'
              },
              sorter: (a, b) => (a.M3 || 0) - (b.M3 || 0),
            },
            {
              title: 'Cidade',
              dataIndex: 'cidade',
              key: 'cidade',
              width: 120,
              ellipsis: true,
            },
            {
              title: 'UF',
              dataIndex: 'uf',
              key: 'uf',
              width: 60,
              align: 'center',
            },
            {
              title: 'CEP',
              dataIndex: 'CEPENTREGA',
              key: 'CEPENTREGA',
              width: 100,
              render: (cep) => {
                if (!cep) return '-'
                return cep.replace(/(\d{5})(\d{3})/, '$1-$2')
              },
            },
            {
              title: 'Inclusão',
              dataIndex: 'inclusao',
              key: 'inclusao',
              width: 90,
              align: 'center',
              render: (text) => {
                const isManual = text === 'MANUAL'
                return (
                  <span
                    style={{
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      backgroundColor: isManual ? '#52c41a' : '#1890ff',
                      color: '#fff',
                    }}
                  >
                    {text || 'LEITURA'}
                  </span>
                )
              },
              filters: [
                { text: 'Manual', value: 'MANUAL' },
                { text: 'Leitura', value: 'LEITURA' },
              ],
              onFilter: (value, record) => (record.inclusao || 'LEITURA') === value,
            },
            {
              title: 'Alterações',
              dataIndex: 'alteracoes',
              key: 'alteracoes',
              width: 200,
              render: (alteracoes) => {
                try {
                  // Parse das alterações se for string JSON
                  const alteracoesParsed = typeof alteracoes === 'string'
                    ? JSON.parse(alteracoes)
                    : alteracoes

                  if (!alteracoesParsed || !Array.isArray(alteracoesParsed) || alteracoesParsed.length === 0) {
                    return <span style={{ color: '#999', fontStyle: 'italic' }}>Sem alterações</span>
                  }

                  return (
                    <div style={{ maxHeight: '100px', overflowY: 'auto' }}>
                      {alteracoesParsed.map((alt, index) => (
                        <div key={index} style={{
                          marginBottom: '4px',
                          padding: '2px 4px',
                          backgroundColor: currentTheme === 'dark' ? '#2f2f2f' : '#f0f0f0',
                          border: currentTheme === 'dark' ? '1px solid #444444' : '1px solid #d9d9d9',
                          borderRadius: '3px',
                          fontSize: '10px'
                        }}>
                          <div><strong style={{ color: currentTheme === 'dark' ? '#ffffff' : '#333333' }}>{alt.acao || 'N/A'}</strong></div>
                          <div style={{ color: currentTheme === 'dark' ? '#cccccc' : '#333333' }}>De: {alt.antes || 'N/A'} → Para: {alt.depois || 'N/A'}</div>
                          <div style={{ color: currentTheme === 'dark' ? '#888888' : '#666666' }}>
                            {alt.usuario} - {new Date(alt.data).toLocaleString('pt-BR')}
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                } catch (error) {
                  console.warn('Erro ao fazer parse das alterações na coluna:', error)
                  return <span style={{ color: '#f00', fontStyle: 'italic' }}>Erro ao carregar alterações</span>
                }
              },
            }
          ]}
        />
      </Modal>

      {/* Modal de Gestão de Lotes */}
      {modalLotesVisible && (
        <ModalLotes
          visible={modalLotesVisible}
          onClose={fecharModalLotes}
          carro={carroSelecionadoLotes}
          dadosCarroLotes={dadosCarroLotes}
          selectedDate={selectedDate ? selectedDate.format('YYYY-MM-DD') : ''}
        />
      )}

      {/* Modal de Detalhes da Cubagem */}
      <Modal
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: '24px' }}>
            <span>Detalhes da Cubagem - {selectedDate ? selectedDate.format('DD/MM/YYYY') : ''}</span>
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              onClick={() => handlePrintCubagem(getFilteredCubagemData())}
            >
              Imprimir Relatório
            </Button>
          </div>
        }
        open={cubagemModalVisible}
        onCancel={() => setCubagemModalVisible(false)}
        width={900}
        footer={[
          <Button key="close" onClick={() => setCubagemModalVisible(false)}>
            Fechar
          </Button>
        ]}
        styles={{
          header: {
            backgroundColor: currentTheme === 'dark' ? '#111111' : undefined,
            color: currentTheme === 'dark' ? '#ffffff' : undefined,
            padding: '16px 24px',
            borderBottom: currentTheme === 'dark' ? '1px solid #333333' : undefined,
          },
          body: {
            backgroundColor: currentTheme === 'dark' ? '#1f1f1f' : undefined,
            padding: '24px',
          }
        }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Row gutter={16}>
            <Col span={12}>
              <Input
                placeholder="Filtrar por Cliente"
                value={cubagemFilters.cliente}
                onChange={(e) => setCubagemFilters({ ...cubagemFilters, cliente: e.target.value })}
                allowClear
              />
            </Col>
            <Col span={12}>
              <Input
                placeholder="Filtrar por Nota ou Chave"
                value={cubagemFilters.nota}
                onChange={(e) => setCubagemFilters({ ...cubagemFilters, nota: e.target.value })}
                allowClear
              />
            </Col>
          </Row>

          <Table
            columns={columnsCubagem}
            dataSource={getFilteredCubagemData()}
            rowKey={(record) => `${record.chave}-${record.id}`}
            size="small"
            pagination={{ pageSize: 10 }}
            scroll={{ y: 400 }}
            locale={{ emptyText: 'Nenhum dado de cubagem encontrado' }}
            summary={(pageData) => {
              let totalM3 = 0;
              pageData.forEach(({ metragem_total }) => {
                totalM3 += Number(metragem_total) || 0;
              });

              return (
                <Table.Summary.Row style={{ backgroundColor: currentTheme === 'dark' ? '#111111' : '#fafafa' }}>
                  <Table.Summary.Cell index={0} colSpan={4} style={{ fontWeight: 'bold', textAlign: 'right' }}>
                    Total Cúbico (m³):
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} style={{ fontWeight: 'bold' }}>
                    {totalM3.toFixed(4)}
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              );
            }}
          />
        </Space>
      </Modal>

    </>
  )

  if (embedded) {
    return coreContent
  }



  return (
    <div className="painel-operacional">
      <Navbar onMenuClick={handleMobileMenuClick} />
      <Layout style={{ marginTop: 40 }}>
        <Sidebar
          visible={sidebarVisible}
          onClose={() => setSidebarVisible(false)}
          onMenuClick={({ key }) => {
            if (key === 'operacional') navigate('/painel-operacional')
            if (key === 'expedicao') navigate('/painel-expedicao')
            if (key === 'relatorios') navigate('/relatorios')
          }}
        />
        <Layout className={`painel-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
          <Content className="content-body">
            {coreContent}
          </Content>
        </Layout>
      </Layout>
    </div>
  )
}

export default PainelExpedicao
